import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Hammer, Plus, Search, Settings as SettingsIcon, CalendarDays, List, CalendarClock, MapPin, Users } from 'lucide-react';
import '../components/delivery/delivery.css';
import '../components/upgrades/upgrades.css';
import { StatusBadge, FlagChips } from '../components/upgrades/StatusBadge';
import NewUpgradeModal from '../components/upgrades/NewUpgradeModal';
import UpgradeCalendar from '../components/upgrades/UpgradeCalendar';
import UpgradeSettingsModal from '../components/upgrades/UpgradeSettingsModal';
import { UPGRADE_STATUSES, CLOSED_STATUSES } from '../constants/upgrades';
import { api, dateRange, initials, moneyShort } from '../utils/upgradesApi';

const JobCard = ({ job, onOpen }) => {
  const pct = job.item_count ? Math.round((job.done_count / job.item_count) * 100) : 0;
  return (
    <button type="button" className="upg-job" onClick={() => onOpen(job.id)}>
      <div className="upg-job__top">
        <span className="upg-job__number">{job.number}</span>
        <span className="upg-job__title">{job.title}</span>
        <StatusBadge status={job.status} />
        <FlagChips flags={job.flags} />
        <span className="upg-job__price">{moneyShort(job.totals.gross)}</span>
      </div>
      <div className="upg-job__meta">
        <strong>{job.customer_name}</strong>
        {job.machines.map((m) => <span key={m.machine_id} className="upg-tag">{m.model_type} · SN {m.serial_number}</span>)}
        {job.location && <span><MapPin size={14} aria-hidden="true" /> {job.location}</span>}
        <span><CalendarClock size={14} aria-hidden="true" /> {dateRange(job)}{job.arrival_time ? `, ${job.arrival_time}` : ''}</span>
        {job.crew.length > 0 && (
          <span title={job.crew.map((c) => c.name).join(', ')}>
            <Users size={14} aria-hidden="true" />
            <span className="upg-avatars" aria-label={`Jede: ${job.crew.map((c) => c.name).join(', ')}`}>
              {job.crew.map((c) => <span key={c.user_id} className={`upg-avatar ${c.is_lead ? 'upg-avatar--lead' : ''}`}>{initials(c.name)}</span>)}
            </span>
          </span>
        )}
      </div>
      {job.item_count > 0 && ['naplanovano', 'probiha', 'dokonceno'].includes(job.status) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
          <div className="upg-progress" style={{ flex: 1 }} aria-hidden="true"><span style={{ width: `${pct}%` }} /></div>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{job.done_count}/{job.item_count}</span>
        </div>
      )}
    </button>
  );
};

// Overview of upgrade jobs: pipeline by status, list and month calendar.
const Upgrades = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState(null);
  const [meta, setMeta] = useState({ templates: [], me: {} });
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [mine, setMine] = useState(false);
  const [view, setView] = useState('list');
  const [showNew, setShowNew] = useState(Boolean(params.get('new')));
  const [initialQuery] = useState(params.get('new') || '');
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(() => {
    Promise.all([api('/'), api('/meta')])
      .then(([list, m]) => { setJobs(list); setMeta(m); })
      .catch((e) => setError(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (id) => navigate(`/upravy/${id}`);
  const closeNew = () => { setShowNew(false); if (params.get('new')) setParams({}, { replace: true }); };
  const isManager = ['admin', 'manager'].includes(meta.me?.role);

  const counts = useMemo(() => Object.fromEntries(UPGRADE_STATUSES.map((s) => [s.value, (jobs || []).filter((j) => j.status === s.value).length])), [jobs]);
  // search, "only mine" and the status chip apply to both views; the list also hides closed jobs unless their chip is chosen
  const matching = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (jobs || []).filter((j) => {
      if (status && j.status !== status) return false;
      if (mine && !j.crew.some((c) => c.user_id === meta.me?.id)) return false;
      if (!needle) return true;
      return `${j.number} ${j.title} ${j.customer_name} ${j.location || ''} ${j.machines.map((m) => m.serial_number).join(' ')}`.toLowerCase().includes(needle);
    });
  }, [jobs, status, q, mine, meta.me]);
  const visible = useMemo(() => (status ? matching : matching.filter((j) => !CLOSED_STATUSES.includes(j.status))), [matching, status]);

  const attention = (jobs || []).filter((j) => j.flags.some((f) => f !== 'starts_soon')).length;
  const openCount = (jobs || []).filter((j) => !CLOSED_STATUSES.includes(j.status)).length;

  return (
    <div className="dlv upg-wrap">
      <div className="dlv-hero" style={{ marginBottom: 16 }}>
        <div className="dlv-hero__kicker">Servis · Úpravy u zákazníka</div>
        <h1 className="dlv-hero__title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Hammer /> Úpravy trenažérů</h1>
        <div className="dlv-hero__meta">
          <span>Rozpracované <strong>{openCount}</strong></span>
          <span>Čeká na naplánování <strong>{counts.schvaleno}</strong></span>
          <span>K vyřízení <strong>{attention}</strong></span>
        </div>
        <div className="dlv-hero__badge" style={{ display: 'flex', gap: 8 }}>
          {isManager && (
            <button type="button" className="dlv-btn" style={{ background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
              onClick={() => setShowSettings(true)} aria-label="Nastavení modulu" title="Nastavení modulu"><SettingsIcon size={18} /></button>
          )}
          <button type="button" className="dlv-btn dlv-btn--primary" onClick={() => setShowNew(true)}><Plus size={18} /> Nová úprava</button>
        </div>
      </div>

      <div className="upg-pipeline" role="group" aria-label="Filtr podle stavu">
        {UPGRADE_STATUSES.map((s) => (
          <button key={s.value} type="button" className={`upg-stage ${status === s.value ? 'upg-stage--on' : ''}`} aria-pressed={status === s.value}
            onClick={() => setStatus(status === s.value ? '' : s.value)}>
            <span className="upg-stage__n">{counts[s.value]}</span>
            <span className="upg-stage__l">{s.label}</span>
            <span className="upg-stage__bar" style={{ background: s.color, opacity: counts[s.value] ? 1 : 0.2 }} />
          </button>
        ))}
      </div>

      <div className="upg-toolbar">
        <div className="grow">
          <Search size={16} aria-hidden="true" />
          <input className="dlv-input" aria-label="Hledat úpravu" placeholder="Zákazník, sériové číslo, číslo úpravy…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <label className="upg-check"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Jen moje výjezdy</label>
        <div className="upg-seg" role="group" aria-label="Zobrazení">
          <button type="button" aria-pressed={view === 'list'} onClick={() => setView('list')}><List size={16} /> Seznam</button>
          <button type="button" aria-pressed={view === 'calendar'} onClick={() => setView('calendar')}><CalendarDays size={16} /> Kalendář</button>
        </div>
      </div>

      {error && <p role="alert" className="upg-error">{error}</p>}
      {!jobs && !error && <p className="dlv-hint">Načítám…</p>}

      {jobs && view === 'list' && (
        visible.length === 0
          ? <p className="dlv-hint">{jobs.length === 0 ? 'Zatím žádná úprava. Založte ji tlačítkem „Nová úprava“ nebo v detailu trenažéru.' : 'Nic neodpovídá filtru.'}</p>
          : <div style={{ display: 'grid', gap: 10 }}>{visible.map((j) => <JobCard key={j.id} job={j} onOpen={open} />)}</div>
      )}
      {jobs && view === 'calendar' && <UpgradeCalendar jobs={matching} onOpen={open} />}

      {showNew && <NewUpgradeModal onClose={closeNew} templates={meta.templates} initialQuery={initialQuery} onCreated={open} />}
      {showSettings && <UpgradeSettingsModal onClose={() => setShowSettings(false)} onSaved={load} />}
    </div>
  );
};

export default Upgrades;
